import { useEffect } from 'react';
import './IcePunchAnimation.css';

/**
 * PROFESSIONAL ICE PUNCH ANIMATION
 * Ice-type physical move - punches with a fist of ice
 * 
 * Key Features:
 * - Ice-type physical attack (75 base power, 100% accuracy)
 * - 10% chance to freeze
 * - Icy fist dash with frost contact impact
 * - Professional gaming quality with crystalline punch effects
 */
export default function IcePunchAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const dx = opponentX - playerX;
    const dy = opponentY - playerY;

    const elements = [];

    // PHASE 1: Frost Fist Charge (0-350ms)
    playerEl.classList.add('ice-punch-user-charge');

    const frostFist = document.createElement('div');
    frostFist.className = 'ice-punch-frost-fist';
    frostFist.style.left = `${playerX - 25}px`;
    frostFist.style.top = `${playerY - 25}px`;
    container.appendChild(frostFist);
    elements.push(frostFist);

    // Ice crystals forming around fist
    for (let i = 0; i < 8; i++) {
      const crystal = document.createElement('div');
      crystal.className = 'ice-punch-charge-crystal';
      const angle = (i / 8) * 360;
      const radius = 40;
      crystal.style.left = `${playerX + Math.cos(angle * Math.PI / 180) * radius - 4}px`;
      crystal.style.top = `${playerY + Math.sin(angle * Math.PI / 180) * radius - 4}px`;
      crystal.style.animationDelay = `${i * 35}ms`;
      container.appendChild(crystal);
      elements.push(crystal);
    }

    // PHASE 2: Ice Dash (350-650ms)
    const phase2Timer = setTimeout(() => {
      playerEl.classList.add('ice-punch-user-dash');
      playerEl.style.setProperty('--dash-x', `${dx * 0.8}px`);
      playerEl.style.setProperty('--dash-y', `${dy * 0.8}px`);

      // Frost trail
      for (let i = 0; i < 8; i++) {
        const trailTimer = setTimeout(() => {
          const trail = document.createElement('div');
          trail.className = 'ice-punch-frost-trail';
          const progress = i / 8;
          trail.style.left = `${playerX + dx * progress * 0.8 + (Math.random() - 0.5) * 15 - 6}px`;
          trail.style.top = `${playerY + dy * progress * 0.8 + (Math.random() - 0.5) * 15 - 6}px`;
          container.appendChild(trail);
          elements.push(trail);
        }, i * 25);
        elements.push({ timer: trailTimer, type: 'timer' });
      }
    }, 350);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Freezing Impact (650-1100ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('ice-punch-opponent-hit');
      playerEl.classList.remove('ice-punch-user-dash');

      // Ice impact flash
      const impact = document.createElement('div');
      impact.className = 'ice-punch-impact';
      impact.style.left = `${opponentX - 80}px`;
      impact.style.top = `${opponentY - 80}px`;
      container.appendChild(impact);
      elements.push(impact);

      // Frost shockwaves
      for (let i = 0; i < 3; i++) {
        const swTimer = setTimeout(() => {
          const sw = document.createElement('div');
          sw.className = 'ice-punch-shockwave';
          sw.style.left = `${opponentX - 60}px`;
          sw.style.top = `${opponentY - 60}px`;
          container.appendChild(sw);
          elements.push(sw);
        }, i * 80);
        elements.push({ timer: swTimer, type: 'timer' });
      }

      // Ice shard burst
      for (let i = 0; i < 14; i++) {
        const shardAngle = (i / 14) * 360;
        const shardDist = 40 + Math.random() * 40;
        const shard = document.createElement('div');
        shard.className = 'ice-punch-shard';
        shard.style.left = `${opponentX - 4}px`;
        shard.style.top = `${opponentY - 4}px`;
        shard.style.setProperty('--scatter-x', `${Math.cos(shardAngle * Math.PI / 180) * shardDist}px`);
        shard.style.setProperty('--scatter-y', `${Math.sin(shardAngle * Math.PI / 180) * shardDist}px`);
        shard.style.animationDelay = `${i * 15}ms`;
        container.appendChild(shard);
        elements.push(shard);
      }

      // Frost mist at impact point
      for (let i = 0; i < 6; i++) {
        const mist = document.createElement('div');
        mist.className = 'ice-punch-frost-mist';
        mist.style.left = `${opponentX + (Math.random() - 0.5) * 60 - 10}px`;
        mist.style.top = `${opponentY + (Math.random() - 0.5) * 40 - 10}px`;
        mist.style.animationDelay = `${i * 30}ms`;
        container.appendChild(mist);
        elements.push(mist);
      }
    }, 650);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Frost Settle (1100-1400ms)
    const phase4Timer = setTimeout(() => {
      playerEl.classList.remove('ice-punch-user-charge');

      for (let i = 0; i < 5; i++) {
        const settle = document.createElement('div');
        settle.className = 'ice-punch-frost-settle';
        settle.style.left = `${opponentX + (Math.random() - 0.5) * 60 - 5}px`;
        settle.style.top = `${opponentY + (Math.random() - 0.5) * 40 - 5}px`;
        settle.style.animationDelay = `${i * 40}ms`;
        container.appendChild(settle);
        elements.push(settle);
      }
    }, 1100);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (1400ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('ice-punch-user-charge', 'ice-punch-user-dash');
      opponentEl.classList.remove('ice-punch-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 1400);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('ice-punch-user-charge', 'ice-punch-user-dash');
      opponentEl.classList.remove('ice-punch-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

IcePunchAnimation.moveType = 'ice';
